import { ArrowUpRight } from '@phosphor-icons/react/dist/ssr/ArrowUpRight'
import essayIcon from '@/assets/icons/essay.svg'
import { getAllCaseStudies, getAllNotes } from '@/data/contentService'
import type { CaseStudy, Note } from '@/domain/content'
import { resolveCaseStudyClientLogo } from '@/lib/caseStudyClientLogo'
import { caseStudyBlogPath } from '@/lib/caseStudyPublicUrl'
import { formatAccelerationDisplay, parseLikert5FromText } from '@/lib/caseStudyHeroFormat'
import { isPublicCaseStudy } from '@/lib/caseStudyVisibility'

const READ_ALSO_METRIC_LABEL_CLASSNAME =
  'text-[11px] font-semibold uppercase leading-[16.5px] tracking-[0.275px] text-[var(--foreground-muted)]'

const ReadAlsoMetricBars = ({ filled }: { filled: number }) => {
  const count = Math.max(0, Math.min(5, Math.round(filled)))
  return (
    <div className="flex items-center gap-px" aria-hidden>
      {Array.from({ length: 5 }, (_, i) => (
        <span
          key={i}
          className={`h-5 w-[3px] rounded-full ${
            i < count ? 'bg-[#202020]' : 'box-border border border-[#cecece] bg-transparent'
          }`}
        />
      ))}
    </div>
  )
}

const caseStudyHref = (cs: CaseStudy) => caseStudyBlogPath(cs.page.slug)
const caseStudyTitle = (cs: CaseStudy) => cs.page.title
const caseStudyClient = (cs: CaseStudy) => cs.hero?.client ?? 'Unknown client'

const clientInitials = (client: string) => {
  const parts = client
    .replace(/[^a-zA-Z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
  if (parts.length === 0) {
    return 'CS'
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase()
  }
  return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase()
}

const cardTags = (cs: CaseStudy, max = 2) => {
  if (cs.hero?.tags?.length) {
    return cs.hero.tags.slice(0, max)
  }
  if (cs.tags.length) {
    return cs.tags.slice(0, max)
  }
  return ['workflow']
}

const HOME_CASE_STUDY_ORDER = [
  'united-accountants-one-to-one-follow-up-pages', // United Accountants 1:1 follow-up
  'united-accountants-custom-landing-pages-gtm-system', // United Accountants
  'personalized-outreach-visuals-workflow', // kova.ee
] as const

const caseStudySlug = (cs: CaseStudy) => cs.page.slug
const HOME_NOTE_ORDER = ['broken-handoff-between-sales-and-website'] as const
const FEATURED_BLOG_SLUG = 'united-accountants-one-to-one-follow-up-pages'

type Props = {
  title?: string
  description?: string
  items?: CaseStudy[]
  notes?: Note[]
  showExploreMore?: boolean
  layout?: 'home' | 'index'
  padded?: boolean
  variant?: 'default' | 'readAlso'
  stackOnMobile?: boolean
}

type BlogGridItem =
  | {
      type: 'case-study'
      shuffleKey: string
      item: CaseStudy
    }
  | {
      type: 'note'
      shuffleKey: string
      item: Note
    }

const stableShuffleScore = (value: string) => {
  let hash = 0
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) % 9973
  }
  return hash
}

const spreadNotesIntoCaseStudies = (caseStudyEntries: BlogGridItem[], noteEntries: BlogGridItem[]) => {
  if (noteEntries.length === 0) {
    return caseStudyEntries
  }

  const mixed = [...caseStudyEntries]
  const slotSize = Math.max(2, Math.floor((caseStudyEntries.length + noteEntries.length) / (noteEntries.length + 1)))

  noteEntries.forEach((noteEntry, index) => {
    const targetIndex = Math.min(mixed.length, slotSize * (index + 1))
    mixed.splice(targetIndex, 0, noteEntry)
  })

  return mixed
}

export const CaseStudyCard = ({
  cs,
  compact = false,
  fixedHeight = true,
  tagLimit = 2,
  className = '',
}: {
  cs: CaseStudy
  compact?: boolean
  fixedHeight?: boolean
  tagLimit?: number
  className?: string
}) => {
  const client = caseStudyClient(cs)
  const logo = resolveCaseStudyClientLogo(client)
  const acceleration = cs.hero?.acceleration
  const aiHandoff = cs.hero?.ai_handoff_potential
  const replicability = cs.hero?.replicability
  const tags = cardTags(cs, tagLimit)
  const heightClass = fixedHeight ? 'h-[370px] min-h-[370px]' : ''
  const titleHeightClass = fixedHeight ? 'min-h-[120px]' : ''

  if (compact) {
    return (
      <a
        href={caseStudyHref(cs)}
        className="group block rounded-[16px] border border-[#e0e0e0] bg-white px-[11px] pt-[10px] pb-[12px] transition-[box-shadow,transform] duration-200 [transition-timing-function:cubic-bezier(0.2,0,0,1)] hover:shadow-[0px_1px_1px_rgba(0,0,0,0.04),0px_18px_34px_rgba(0,0,0,0.04)] active:scale-[0.998] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2"
      >
        <div className="flex items-start gap-2">
          <span className="relative size-6 shrink-0 overflow-hidden rounded-[4px]">
            {logo ? (
              <img src={logo} alt={`${client} logo`} className="size-full object-contain" width={24} height={24} />
            ) : (
              <span className="inline-flex size-full items-center justify-center bg-[#202020] text-[10px] font-semibold text-white">
                {clientInitials(client)}
              </span>
            )}
          </span>
          <span className="text-[15px] leading-[22.5px] tracking-[-0.16px] text-[var(--foreground-muted)]">
            {client}
          </span>
        </div>
        <div className="mt-[10px] font-['Familjen_Grotesk'] text-[16px] leading-5 tracking-[-0.16px] text-[var(--foreground)] [text-wrap:pretty]">
          {caseStudyTitle(cs)}
        </div>
        <div className="mt-[10px] flex flex-wrap items-end justify-start gap-x-1 gap-y-1">
          {tags.map((tag, tagIdx) => (
            <span
              key={`${caseStudySlug(cs)}-${tag}-${tagIdx}`}
              className="inline-flex h-[26px] shrink-0 items-center justify-center rounded-full border border-[#d6d6d6] bg-white px-2.5 text-center text-[12px] leading-3 whitespace-nowrap text-[var(--foreground)]"
            >
              {tag}
            </span>
          ))}
        </div>
      </a>
    )
  }

  return (
    <a
      href={caseStudyHref(cs)}
      className={`group flex ${heightClass} flex-col gap-[10px] overflow-clip rounded-[16px] border border-[#e0e0e0] bg-[#fafafa] px-[11px] pt-[10px] pb-[12px] transition-[box-shadow,transform] duration-200 [transition-timing-function:cubic-bezier(0.2,0,0,1)] hover:shadow-[0px_1px_1px_rgba(0,0,0,0.04),0px_18px_34px_rgba(0,0,0,0.04)] active:scale-[0.998] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2 ${className}`}
    >
      <div className="flex items-start gap-2">
        <span className="relative size-6 shrink-0 overflow-hidden rounded-[4px]">
          {logo ? (
            <img src={logo} alt={`${client} logo`} className="size-full object-contain" width={24} height={24} />
          ) : (
            <span className="inline-flex size-full items-center justify-center bg-[#202020] text-[10px] font-semibold text-white">
              {clientInitials(client)}
            </span>
          )}
        </span>
        <span className="text-[15px] leading-[22.5px] tracking-[-0.16px] text-[var(--foreground-muted)]">
          {client}
        </span>
      </div>
      <div
        className={`${titleHeightClass} font-['Familjen_Grotesk'] text-[16px] leading-5 tracking-[-0.16px] text-[var(--foreground)] [text-wrap:pretty]`}
      >
        {caseStudyTitle(cs)}
      </div>
      <div className="mt-auto flex flex-col gap-[10px]">
        <div className="w-full">
          <div className="flex items-center gap-1 border-b border-[#e0e0e0] pb-2 pt-[7px]">
            <div className="min-w-[104px]">
              <div className={READ_ALSO_METRIC_LABEL_CLASSNAME}>Acceleration</div>
            </div>
            <div className="flex min-w-0 flex-1 items-center justify-end">
              <span className="text-right text-[14px] font-bold leading-[19.25px] text-[var(--foreground)]">
                {acceleration ? formatAccelerationDisplay(acceleration) : '—'}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1 border-b border-[#e0e0e0] pb-2 pt-[7px]">
            <div className="min-w-[104px]">
              <div className={READ_ALSO_METRIC_LABEL_CLASSNAME}>AI handoff</div>
            </div>
            <div className="flex min-w-0 flex-1 items-center justify-end">
              <ReadAlsoMetricBars filled={parseLikert5FromText(aiHandoff) ?? 0} />
            </div>
          </div>
          <div className="flex items-center gap-1 border-b border-[#e0e0e0] pb-2 pt-[7px]">
            <div className="min-w-[104px]">
              <div className={READ_ALSO_METRIC_LABEL_CLASSNAME}>Replicability</div>
            </div>
            <div className="flex min-w-0 flex-1 items-center justify-end">
              <ReadAlsoMetricBars filled={parseLikert5FromText(replicability) ?? 0} />
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-end justify-start gap-x-1 gap-y-1">
          {tags.map((tag, tagIdx) => (
            <span
              key={`${caseStudySlug(cs)}-${tag}-${tagIdx}`}
              className="inline-flex h-[26px] shrink-0 items-center justify-center rounded-full border border-[#d6d6d6] bg-white px-2.5 text-center text-[12px] leading-3 whitespace-nowrap text-[var(--foreground)]"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </a>
  )
}

export const NoteCard = ({
  note,
  className = '',
  variant = 'default',
}: {
  note: Note
  className?: string
  variant?: 'default' | 'readAlso'
}) => (
  <a
    href={`/blog/${note.slug}`}
    className={`group flex flex-col rounded-[16px] border border-[#e0e0e0] ${variant === 'readAlso' ? 'bg-[#fafafa]' : 'bg-white'} px-[11px] pt-[10px] pb-[12px] transition-[box-shadow,transform] duration-200 [transition-timing-function:cubic-bezier(0.2,0,0,1)] hover:shadow-[0px_1px_1px_rgba(0,0,0,0.04),0px_18px_34px_rgba(0,0,0,0.04)] active:scale-[0.998] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2 ${variant === 'readAlso' ? 'h-[370px] min-h-[370px] justify-between' : ''} ${className}`}
  >
    <div>
      <div className="mb-[10px] flex h-6 items-center justify-between gap-2 text-[12px] leading-6 text-[var(--foreground-muted)]">
        <img src={essayIcon} alt="" className="h-5 w-auto shrink-0" aria-hidden />
        {note.readTime ? (
          <span className="text-[15px] leading-[22.5px] tracking-[-0.16px] text-[var(--foreground-muted)]">
            {note.readTime} min
          </span>
        ) : null}
      </div>
      <h2 className="font-['Familjen_Grotesk'] text-[16px] font-normal leading-5 tracking-[-0.16px] text-[var(--foreground)] [text-wrap:pretty]">
        {note.title}
      </h2>
      <p className="mt-[10px] text-[14px] leading-[1.45] tracking-[-0.14px] text-[var(--foreground-muted)] [text-wrap:pretty]">
        {note.preview ?? note.excerpt}
      </p>
    </div>
    <div className={`${variant === 'readAlso' ? '' : 'mt-10'} flex flex-wrap items-end justify-start gap-x-1 gap-y-1`}>
      {note.tags.slice(0, 4).map((tag) => (
        <span
          key={`${note.slug}-${tag}`}
          className="inline-flex h-[26px] shrink-0 items-center justify-center rounded-full border border-[#d6d6d6] bg-white px-2.5 text-center text-[12px] leading-3 whitespace-nowrap text-[var(--foreground)]"
        >
          {tag}
        </span>
      ))}
    </div>
  </a>
)

export const HomeAiCaseStudies = ({
  title = 'AI Case Studies',
  description,
  items: providedItems,
  notes = [],
  showExploreMore = true,
  layout = 'home',
  padded = true,
  variant = 'default',
  stackOnMobile = false,
}: Props) => {
  const caseStudies = getAllCaseStudies().filter(isPublicCaseStudy)
  const allNotes = getAllNotes()
  const items =
    providedItems?.filter(isPublicCaseStudy) ??
    HOME_CASE_STUDY_ORDER
      .map((slug) => caseStudies.find((cs) => caseStudySlug(cs) === slug))
      .filter((cs): cs is CaseStudy => Boolean(cs))
  const displayNotes =
    notes.length > 0
      ? notes
      : HOME_NOTE_ORDER
          .map((slug) => allNotes.find((note) => note.slug === slug))
          .filter((note): note is Note => Boolean(note))
  if (items.length === 0) {
    return null
  }

  if (layout === 'index') {
    const featuredEntries: BlogGridItem[] = []
    const caseStudyEntries: BlogGridItem[] = items
      .map((item) => ({
        type: 'case-study' as const,
        shuffleKey: `case-study:${caseStudySlug(item)}`,
        item,
      }))
      .filter((entry) => {
        if (caseStudySlug(entry.item) === FEATURED_BLOG_SLUG) {
          featuredEntries.push(entry)
          return false
        }
        return true
      })
      .sort((a, b) => stableShuffleScore(a.shuffleKey) - stableShuffleScore(b.shuffleKey))
    const noteEntries: BlogGridItem[] = displayNotes
      .map((item) => ({
        type: 'note' as const,
        shuffleKey: `note:${item.slug}`,
        item,
      }))
      .sort((a, b) => stableShuffleScore(a.shuffleKey) - stableShuffleScore(b.shuffleKey))
    const blogItems = [...featuredEntries, ...spreadNotesIntoCaseStudies(caseStudyEntries, noteEntries)]

    return (
      <section id="read-also" className="mx-auto w-full max-w-[448px] px-4 pb-24 md:max-w-[629px] md:px-0">
        <h1 className="ui-heading-balance mb-4 font-['Familjen_Grotesk'] text-[20px] font-normal leading-6 tracking-[-0.2px] text-[var(--foreground)]">
          {title}
        </h1>
        {description ? (
          <p className="mb-8 max-w-[56ch] text-[14px] leading-[1.55] tracking-[-0.01em] text-[var(--foreground-muted)]">
            {description}
          </p>
        ) : null}
        <div className="columns-1 [column-gap:10px] md:columns-3 md:w-[629px]">
          {blogItems.map((entry) =>
            entry.type === 'case-study' ? (
              <CaseStudyCard
                key={caseStudySlug(entry.item)}
                cs={entry.item}
                fixedHeight={false}
                tagLimit={4}
                className="mb-2.5 inline-flex w-full break-inside-avoid"
              />
            ) : (
              <NoteCard
                key={entry.item.slug}
                note={entry.item}
                className="mb-2.5 inline-flex w-full break-inside-avoid"
              />
            ),
          )}
        </div>
      </section>
    )
  }

  const useMixedEntries =
    variant === 'readAlso' || (layout === 'home' && !providedItems && notes.length === 0 && displayNotes.length > 0)
  const readAlsoEntries: BlogGridItem[] =
    useMixedEntries
      ? items.flatMap((item, index) => {
          const entries: BlogGridItem[] = [
            {
              type: 'case-study' as const,
              shuffleKey: `case-study:${caseStudySlug(item)}`,
              item,
            },
          ]
          const note = displayNotes[index]
          if (note) {
            entries.push({
              type: 'note' as const,
              shuffleKey: `note:${note.slug}`,
              item: note,
            })
          }
          return entries
        })
      : []
  const readAlsoTopEntries = readAlsoEntries.slice(0, 2)
  const readAlsoLowerCaseStudies =
    useMixedEntries
      ? readAlsoEntries
          .slice(2)
          .filter((entry): entry is Extract<BlogGridItem, { type: 'case-study' }> => entry.type === 'case-study')
          .map((entry) => entry.item)
      : items.slice(2)

  return (
    <section
      id="read-also"
      className={`scroll-mt-28 md:scroll-mt-20 mx-auto w-full max-w-[448px] ${padded ? 'px-4' : ''}`}
    >
      <h2 className="ui-heading-balance mb-4 font-['Familjen_Grotesk'] text-[20px] font-normal leading-6 tracking-[-0.2px] text-[var(--foreground)]">
        {title}
      </h2>
      <div className="space-y-3">
        <div className={`grid gap-2.5 ${stackOnMobile ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-2'}`}>
          {useMixedEntries
            ? readAlsoTopEntries.map((entry) =>
                entry.type === 'case-study' ? (
                  <CaseStudyCard key={caseStudySlug(entry.item)} cs={entry.item} />
                ) : (
                  <NoteCard key={entry.item.slug} note={entry.item} variant="readAlso" />
                ),
              )
            : items.slice(0, 2).map((cs) => (
                <CaseStudyCard key={caseStudySlug(cs)} cs={cs} />
              ))}
        </div>
        {readAlsoLowerCaseStudies.length ? (
          <div className="relative h-[186px] overflow-hidden">
            <div className="space-y-3">
              {[...readAlsoLowerCaseStudies, readAlsoLowerCaseStudies[1] ?? readAlsoLowerCaseStudies[0]]
                .slice(0, 2)
                .map((cs) => (
                  <CaseStudyCard key={`compact-${caseStudySlug(cs)}`} cs={cs} compact />
                ))}
            </div>
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[72px] bg-gradient-to-b from-[rgba(255,255,255,0)] to-white" />
          </div>
        ) : null}
        {showExploreMore ? (
          <a
            href="/work/"
            className="group inline-flex w-full items-center justify-center gap-1.5 rounded-[8px] border border-[#e0e0e0] bg-[#fafafa] px-3 py-1.5 text-[14px] font-medium tracking-[-0.14px] text-[var(--foreground)] transition-[background-color,box-shadow] duration-150 [transition-timing-function:cubic-bezier(0.2,0,0,1)] hover:bg-[var(--background)] hover:shadow-[0_1px_1px_rgba(0,0,0,0.04)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2"
          >
            Explore more
            <ArrowUpRight size={16} weight="regular" aria-hidden />
          </a>
        ) : null}
      </div>
    </section>
  )
}
